import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

const MODULE_DISABLED = /^Module '([^']+)' is not enabled/i;

/**
 * Catches 404s from module-gated endpoints whose body matches
 * `Module 'X' is not enabled for this tenant.` and redirects to the Modules settings page,
 * pre-selecting the module that needs activation.
 *
 * Most navigations are already prevented by `requireModuleEnabled` route guards. This
 * interceptor catches the residual case: AJAX calls from non-module pages that hit a gated
 * endpoint (e.g., a dashboard widget querying competition stats for a Tennis tenant).
 *
 * The error is still propagated downstream so individual subscribers can render their inline
 * banner if they care. The interceptor's job is just the redirect-as-side-effect.
 */
export const moduleDisabledInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  return next(req).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 404) {
        const message = pickMessage(err.error);
        const match = message ? MODULE_DISABLED.exec(message) : null;
        if (match) {
          const moduleKey = match[1];
          // Don't redirect if the user is *already* on /settings/modules — would loop.
          if (!router.url.startsWith('/settings/modules')) {
            router.navigate(['/settings/modules'], { queryParams: { promote: moduleKey } });
          }
        }
      }
      return throwError(() => err);
    }),
  );
};

function pickMessage(payload: unknown): string | null {
  if (typeof payload === 'string') return payload;
  if (payload && typeof payload === 'object') {
    const body = payload as Record<string, unknown>;
    if (typeof body['error'] === 'string') return body['error'];
    if (typeof body['message'] === 'string') return body['message'];
  }
  return null;
}
